/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

import React, { Dispatch, SetStateAction } from 'react';
import { useLanguage } from '@/providers/LanguageProvider';
import { handleError } from '@/utils/handelErrorMessage';
import { toast } from 'sonner';
import { Component } from 'types/api-data';

import { useComponents } from '@/hooks/useComponents';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Icons } from '@/components/icons';

const WorkflowRepoEdit = ({
  component,
  setRepoUrl,
  setRepoLoading,
  setOpenDialog,
}: {
  component: Component;
  setRepoUrl: Dispatch<SetStateAction<string>>;
  setRepoLoading: (value: boolean) => void;
  setOpenDialog: (value: boolean) => void;
}) => {
  const { messages } = useLanguage();
  const compIntl = messages?.ComponentsPage;
  const { componentsActions } = useComponents();

  const handleRemoveUrl = async () => {
    const { properties, ...rest } = component;

    const updatedProperties = { ...properties };
    delete updatedProperties.repository_url;

    const copyComponent =
      Object.keys(updatedProperties).length > 0
        ? { ...rest, properties: updatedProperties }
        : { ...rest };

    try {
      setRepoLoading(true);
      await componentsActions.updateComponent(
        component?.component_name,
        copyComponent
      );
      setRepoUrl('');
      toast.success(compIntl?.deleted_repository_url_successfully);
    } catch (error) {
      handleError(error, compIntl?.error_in_deleting_repository_url);
    } finally {
      setRepoLoading(false);
    }
  };

  const handleEditUrl = () => {
    setOpenDialog(true);
  };

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <p className="cursor-pointer opacity-0 transition-opacity group-hover:opacity-100">
          {Icons.Ellipses()}
        </p>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="min-w-44 cursor-pointer rounded-none bg-white p-0 text-h5 text-black"
      >
        <DropdownMenuItem
          className="flex cursor-pointer items-center gap-2 p-2 hover:bg-gray-100"
          onClick={handleEditUrl}
        >
          {Icons.Edit('text-fill-button-focus')}
          <span>{messages?.General?.edit}</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          className="flex cursor-pointer items-center gap-2 p-2 hover:bg-gray-100"
          onClick={handleRemoveUrl}
        >
          {Icons.TrashIcon('text-fill-button-focus size-4')}
          <span>{messages?.ApplicationForm?.remove}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default WorkflowRepoEdit;
